import React, { useMemo } from "react";
import { migrateNotes, getContrastTextColor, FONT_STACKS, hexToRgba } from '@/lib/utils';
import type {
  InvoiceTemplate,
  InvoicePreviewData,
  HeaderSectionConfig,
  SummarySectionConfig,
  LineItemsSectionConfig,
  TotalsSectionConfig,
  FooterSectionConfig,
  TemplateSection,
} from "@/lib/invoice-template-types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { groupLineItems, resolveGroupLabel, getDescriptionTemplateForGroup } from "@/lib/invoice-group-utils";
import type { GroupByOption } from "@/lib/invoice-template-types";

// ============================================================================
// Builder-format state extraction
// Reads section configs from builder-saved templates (IDs: logo, company, etc.)
// and extracts all toggle/position values needed for rendering.
// ============================================================================

type Position = 'left' | 'center' | 'right';

interface BuilderState {
  showCompanyLogo: boolean;
  logoPosition: Position;
  companyLogo: string;
  showCompanyName: boolean;
  showCompanyAddress: boolean;
  showCompanyPhone: boolean;
  showCompanyVAT: boolean;
  showCompanyRegistration: boolean;
  companyPosition: Position;
  showClientName: boolean;
  showClientAddress: boolean;
  showClientContact: boolean;
  clientPosition: Position;
  showInvoiceNumber: boolean;
  showInvoiceDate: boolean;
  showDueDate: boolean;
  invoiceDetailsPosition: Position;
  showProjectCode: boolean;
  showProjectName: boolean;
  showProjectReference: boolean;
  showProjectDescription: boolean;
  showProjectManager: boolean;
  projectPosition: Position;
  groupBy: GroupByOption;
  groupedRowDescription: string;
  groupedRowDescriptionExpense: string;
  groupedRowDescriptionPurchase: string;
  showDate: boolean;
  showDescription: boolean;
  showCostCentreCode: boolean;
  showCostCentreName: boolean;
  showPerson: boolean;
  showRole: boolean;
  showQty: boolean;
  showRate: boolean;
  showVAT: boolean;
  showVATCode: boolean;
  showAmount: boolean;
  showSubtotal: boolean;
  showTax: boolean;
  showDiscount: boolean;
  showTotal: boolean;
  showBankDetails: boolean;
  showNotes: boolean;
  showTermsConditions: boolean;
  footerPosition: Position;
  notesContent: string;
  /** Raw HTML content of the T&C field (may contain merge chip markup). */
  termsContent: string;
  /** Task 5.1 / 5.6 — show Backing Sheet second page (available regardless of groupBy) */
  includeBackingSheet: boolean;
  /** Task 5.7 / 5.8 — independent column visibility for Backing Sheet (true = show) */
  backingSheetColumns: Partial<Record<string, boolean>>;
  /** Task 5.7 / 5.8 — independent column display order for Backing Sheet */
  backingSheetColumnOrder: string[];
  /** Task 5.7 / 5.8 — independent page orientation for Backing Sheet */
  backingSheetOrientation: 'portrait' | 'landscape';
  sectionOrder: string[];
  // Backing Sheet independent header
  bsShowLogo: boolean;
  bsShowCompanyName: boolean;
  bsShowCompanyAddress: boolean;
  bsShowInvoiceNumber: boolean;
  bsShowInvoiceDate: boolean;
  bsShowClientName: boolean;
  bsShowClientAddress: boolean;
  // Backing Sheet independent footer
  bsShowBankDetails: boolean;
  bsShowNotes: boolean;
  bsShowTermsConditions: boolean;
  /** Task 6.7 — alternate row shading (5% accent tint on odd rows) */
  alternateRowColors: boolean;
  /** Task 6.8 — full grid borders vs horizontal dividers only */
  showBorders: boolean;
}

function extractBuilderState(template: InvoiceTemplate): BuilderState {
  const findById = (id: string) =>
    template.sections.find(s => s.id === id) ??
    template.sections.find(s => s.type === id.replace(/([A-Z])/g, '-$1').toLowerCase()) ??
    template.sections.find(s => s.type === id);

  // field-name → show* key map for array-format columns
  const FIELD_TO_SHOW: Record<string, keyof BuilderState> = {
    date:           'showDate',
    description:    'showDescription',
    costCentreCode: 'showCostCentreCode',
    costCentreName: 'showCostCentreName',
    person:         'showPerson',
    role:           'showRole',
    qty:            'showQty',
    rate:           'showRate',
    net:            'showRate',   // alias used in mock templates
    vat:            'showVAT',
    vatCode:        'showVATCode',
    amount:         'showAmount',
  };

  const state: BuilderState = {
    showCompanyLogo: true,
    logoPosition: 'left',
    companyLogo: template.branding?.logo || '',
    showCompanyName: true,
    showCompanyAddress: true,
    showCompanyPhone: true,
    showCompanyVAT: true,
    showCompanyRegistration: true,
    companyPosition: 'left',
    showClientName: true,
    showClientAddress: true,
    showClientContact: true,
    clientPosition: 'left',
    showInvoiceNumber: true,
    showInvoiceDate: true,
    showDueDate: true,
    invoiceDetailsPosition: 'right',
    showProjectCode: true,
    showProjectName: true,
    showProjectReference: true,
    showProjectDescription: true,
    showProjectManager: true,
    projectPosition: 'left',
    groupBy: 'none' as GroupByOption,
    groupedRowDescription: '',
    groupedRowDescriptionExpense: '',
    groupedRowDescriptionPurchase: '',
    showDate: true,
    showDescription: true,
    showCostCentreCode: true,
    showCostCentreName: true,
    showPerson: true,
    showRole: true,
    showQty: true,
    showRate: true,
    showVAT: true,
    showVATCode: true,
    showAmount: true,
    showSubtotal: true,
    showTax: true,
    showDiscount: true,
    showTotal: true,
    showBankDetails: false,
    showNotes: true,
    showTermsConditions: true,
    footerPosition: 'left',
    notesContent: '',
    termsContent: '',
    includeBackingSheet: false,
    backingSheetColumns: {},
    backingSheetColumnOrder: ['date', 'description', 'person', 'role', 'costCentreCode', 'costCentreName', 'qty', 'net', 'vat', 'vatCode', 'amount'],
    backingSheetOrientation: 'landscape' as const,
    sectionOrder: ['logo', 'company', 'client', 'invoice', 'project', 'lineItems', 'totals', 'footer'],
    alternateRowColors: false, // Task 6.7
    showBorders: true, // Task 6.8
    bsShowLogo: true,
    bsShowCompanyName: true,
    bsShowCompanyAddress: false,
    bsShowInvoiceNumber: true,
    bsShowInvoiceDate: true,
    bsShowClientName: true,
    bsShowClientAddress: false,
    bsShowBankDetails: false,
    bsShowNotes: false,
    bsShowTermsConditions: false,
  };

  const logoSec = findById('logo');
  if (logoSec?.config) {
    const c = logoSec.config as unknown as Record<string, unknown>;
    if ('showLogo' in c) state.showCompanyLogo = c.showLogo as boolean;
    if ('showCompanyLogo' in c) state.showCompanyLogo = c.showCompanyLogo as boolean;
    if ('logoPosition' in c) state.logoPosition = c.logoPosition as Position;
  }

  const companySec = findById('company');
  if (companySec?.config) {
    const c = companySec.config as unknown as Record<string, unknown>;
    if ('showCompanyName' in c) state.showCompanyName = c.showCompanyName as boolean;
    if ('showCompanyAddress' in c) state.showCompanyAddress = c.showCompanyAddress as boolean;
    if ('showPhone' in c) state.showCompanyPhone = c.showPhone as boolean;
    if ('showCompanyPhone' in c) state.showCompanyPhone = c.showCompanyPhone as boolean;
    if ('showVAT' in c) state.showCompanyVAT = c.showVAT as boolean;
    if ('showRegistration' in c) state.showCompanyRegistration = c.showRegistration as boolean;
    if ('position' in c) state.companyPosition = c.position as Position;
  }

  const clientSec = findById('client');
  if (clientSec?.config) {
    const c = clientSec.config as unknown as Record<string, unknown>;
    if ('showClientName' in c) state.showClientName = c.showClientName as boolean;
    if ('showClientAddress' in c) state.showClientAddress = c.showClientAddress as boolean;
    if ('showClientContact' in c) state.showClientContact = c.showClientContact as boolean;
    if ('position' in c) state.clientPosition = c.position as Position;
  }

  const invoiceSec = findById('invoice');
  if (invoiceSec?.config) {
    const c = invoiceSec.config as unknown as Record<string, unknown>;
    if ('showInvoiceNumber' in c) state.showInvoiceNumber = c.showInvoiceNumber as boolean;
    if ('showInvoiceDate' in c) state.showInvoiceDate = c.showInvoiceDate as boolean;
    if ('showDueDate' in c) state.showDueDate = c.showDueDate as boolean;
    if ('position' in c) state.invoiceDetailsPosition = c.position as Position;
  }

  const projectSec = findById('project');
  if (projectSec?.config) {
    const c = projectSec.config as unknown as Record<string, unknown>;
    if ('showProjectCode' in c) state.showProjectCode = c.showProjectCode as boolean;
    if ('showProjectName' in c) state.showProjectName = c.showProjectName as boolean;
    if ('showProjectReference' in c) state.showProjectDescription = c.showProjectReference as boolean;
    if ('showProjectDescription' in c) state.showProjectDescription = c.showProjectDescription as boolean;
    if ('showProjectManager' in c) state.showProjectManager = c.showProjectManager as boolean;
    if ('position' in c) state.projectPosition = c.position as Position;
  }

  const lineItemsSec = findById('lineItems');
  if (lineItemsSec?.config) {
    const c = lineItemsSec.config as unknown as Record<string, unknown>;
    if ('groupBy' in c) state.groupBy = c.groupBy as GroupByOption;
    if ('groupedRowDescription' in c) state.groupedRowDescription = c.groupedRowDescription as string;
    if ('groupedRowDescriptionExpense' in c) state.groupedRowDescriptionExpense = c.groupedRowDescriptionExpense as string;
    if ('groupedRowDescriptionPurchase' in c) state.groupedRowDescriptionPurchase = c.groupedRowDescriptionPurchase as string;
    if ('includeBackingSheet' in c) state.includeBackingSheet = c.includeBackingSheet as boolean;
    // Task 5.8 — extract independent backing sheet config
    if ('backingSheetColumns' in c && c.backingSheetColumns && typeof c.backingSheetColumns === 'object') {
      state.backingSheetColumns = c.backingSheetColumns as Partial<Record<string, boolean>>;
    }
    if ('backingSheetColumnOrder' in c && Array.isArray(c.backingSheetColumnOrder) && (c.backingSheetColumnOrder as unknown[]).length > 0) {
      state.backingSheetColumnOrder = c.backingSheetColumnOrder as string[];
    }
    if ('backingSheetOrientation' in c) state.backingSheetOrientation = c.backingSheetOrientation as 'portrait' | 'landscape';
    if ('alternateRowColors' in c) state.alternateRowColors = c.alternateRowColors as boolean; // Task 6.7
    if ('showBorders' in c) state.showBorders = c.showBorders as boolean; // Task 6.8
    if ('bsShowLogo' in c) state.bsShowLogo = c.bsShowLogo as boolean;
    if ('bsShowCompanyName' in c) state.bsShowCompanyName = c.bsShowCompanyName as boolean;
    if ('bsShowCompanyAddress' in c) state.bsShowCompanyAddress = c.bsShowCompanyAddress as boolean;
    if ('bsShowInvoiceNumber' in c) state.bsShowInvoiceNumber = c.bsShowInvoiceNumber as boolean;
    if ('bsShowInvoiceDate' in c) state.bsShowInvoiceDate = c.bsShowInvoiceDate as boolean;
    if ('bsShowClientName' in c) state.bsShowClientName = c.bsShowClientName as boolean;
    if ('bsShowClientAddress' in c) state.bsShowClientAddress = c.bsShowClientAddress as boolean;
    if ('bsShowBankDetails' in c) state.bsShowBankDetails = c.bsShowBankDetails as boolean;
    if ('bsShowNotes' in c) state.bsShowNotes = c.bsShowNotes as boolean;
    if ('bsShowTermsConditions' in c) state.bsShowTermsConditions = c.bsShowTermsConditions as boolean;
    if ('columns' in c && c.columns) {
      if (Array.isArray(c.columns)) {
        // Array format: [{ id, field, visible }] — used by all mock templates
        // First set ALL columns to false so only explicitly visible ones show
        const s = state as unknown as Record<string, unknown>;
        (Object.values(FIELD_TO_SHOW) as (keyof BuilderState)[]).forEach(key => { s[key] = false; });
        (c.columns as Array<{ id?: string; field?: string; visible?: boolean }>).forEach(col => {
          const fieldKey = col.field ?? col.id ?? '';
          const stateKey = FIELD_TO_SHOW[fieldKey];
          if (stateKey) s[stateKey] = col.visible ?? true;
        });
      } else if (typeof c.columns === 'object' && !Array.isArray(c.columns)) {
        // Object/dictionary format: { date: true, role: false } — used by builder-saved templates
        const cols = c.columns as Record<string, boolean>;
        if ('date' in cols) state.showDate = cols.date;
        if ('description' in cols) state.showDescription = cols.description;
        if ('costCentreCode' in cols) state.showCostCentreCode = cols.costCentreCode;
        if ('costCentreName' in cols) state.showCostCentreName = cols.costCentreName;
        if ('person' in cols) state.showPerson = cols.person;
        if ('role' in cols) state.showRole = cols.role;
        if ('qty' in cols) state.showQty = cols.qty;
        if ('rate' in cols) state.showRate = cols.rate;
        if ('vat' in cols) state.showVAT = cols.vat;
        if ('vatCode' in cols) state.showVATCode = cols.vatCode;
        if ('amount' in cols) state.showAmount = cols.amount;
      }
    }
  }

  const totalsSec = findById('totals');
  if (totalsSec?.config) {
    const c = totalsSec.config as unknown as Record<string, unknown>;
    if ('showSubtotal' in c) state.showSubtotal = c.showSubtotal as boolean;
    if ('showTax' in c) state.showTax = c.showTax as boolean;
    if ('showDiscount' in c) state.showDiscount = c.showDiscount as boolean;
    if ('showTotal' in c) state.showTotal = c.showTotal as boolean;
  }

  const footerSec = findById('footer');
  if (footerSec?.config) {
    const c = footerSec.config as unknown as Record<string, unknown>;
    if ('showBankDetails' in c) state.showBankDetails = c.showBankDetails as boolean;
    if ('showNotes' in c) state.showNotes = c.showNotes as boolean;
    if ('showTermsConditions' in c) state.showTermsConditions = c.showTermsConditions as boolean;
    if ('showTermsAndConditions' in c) state.showTermsConditions = c.showTermsAndConditions as boolean;
    if ('notesContent' in c) state.notesContent = c.notesContent as string;
    if ('notes' in c && !('notesContent' in c)) state.notesContent = c.notes as string;
    // Task 4.3 — extract T&C HTML content
    if ('termsAndConditions' in c) state.termsContent = c.termsAndConditions as string;
    if ('termsContent' in c) state.termsContent = c.termsContent as string;
    if ('position' in c) state.footerPosition = c.position as Position;
  }

  if (template.branding?.logoPosition) {
    state.logoPosition = template.branding.logoPosition;
  }

  // Override groupBy from top-level settings (matches builder's extractTemplateState pattern)
  if (template.settings?.groupBy) {
    state.groupBy = template.settings.groupBy as GroupByOption;
  }

  const builderIds = new Set(['logo', 'company', 'client', 'invoice', 'project', 'lineItems', 'totals', 'footer']);
  const ordered = template.sections
    .filter(s => builderIds.has(s.id))
    .sort((a, b) => a.order - b.order)
    .map(s => s.id);
  if (ordered.length > 0) {
    state.sectionOrder = ordered;
  }

  return state;
}

// ============================================================================
// Helper: position classes
// ============================================================================

function positionClasses(position: Position): { flex: string; text: string } {
  switch (position) {
    case 'center':
      return { flex: 'justify-center', text: 'text-center' };
    case 'right':
      return { flex: 'justify-end', text: 'text-right' };
    default:
      return { flex: 'justify-start', text: '' };
  }
}

// ============================================================================
// InvoicePreview Component
// Detects builder-format vs standard-format and routes to the correct renderer.
// ============================================================================

interface InvoicePreviewProps {
  template: InvoiceTemplate;
  previewData: InvoicePreviewData;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default column order — Task 2.8
// ─────────────────────────────────────────────────────────────────────────────
const PREVIEW_DEFAULT_COLUMN_ORDER = [
  'date', 'description', 'person', 'role', 'qty', 'net', 'vat', 'vatCode', 'amount',
  'costCentreCode', 'costCentreName',
];

export function InvoicePreview({ template, previewData }: InvoicePreviewProps) {
  const isBuilderFormat = useMemo(() => {
    const builderIds = new Set(['logo', 'company', 'client', 'invoice', 'project', 'lineItems', 'totals', 'footer']);
    return template.sections.some(s => builderIds.has(s.id));
  }, [template.sections]);

  if (isBuilderFormat) {
    return <BuilderFormatPreview template={template} previewData={previewData} />;
  }

  return <StandardFormatPreview template={template} previewData={previewData} />;
}

// ============================================================================
// Builder Format Preview
// Renders sections individually with position/alignment support,
// matching the builder's own renderPreviewSection layout.
// ============================================================================

function BuilderFormatPreview({ template, previewData }: InvoicePreviewProps) {
  const state = useMemo(() => extractBuilderState(template), [template]);

  // Task 2.3 — orientation from template settings
  const orientation = template.settings?.orientation || 'portrait';
  const paperWidth = orientation === 'landscape' ? '297mm' : '210mm';
  const paperMinHeight = orientation === 'landscape' ? '210mm' : '297mm';

  // Task 6.5c — accent colour from template branding
  const accentBg = template.branding?.primaryColor || '#2563eb';
  const accentText = getContrastTextColor(accentBg);
  // Task 6.7c — 5% accent tint for alternate rows
  const accentRowBg = state.alternateRowColors ? hexToRgba(accentBg, 0.05) : 'transparent';

  // Task 6.6c — font family from template branding
  const fontKey = template.branding?.fontFamily || 'inter';
  const fontStack = FONT_STACKS[fontKey] ?? fontKey;

  // Task 2.8 — column order from template line-items section config
  const columnOrder = useMemo(() => {
    const lineItemsSec = template.sections.find(s => s.id === 'lineItems' || s.type === 'line-items');
    if (lineItemsSec?.config) {
      const c = lineItemsSec.config as unknown as Record<string, unknown>;
      if (Array.isArray(c.columnOrder) && c.columnOrder.length > 0) {
        return c.columnOrder as string[];
      }
    }
    return PREVIEW_DEFAULT_COLUMN_ORDER;
  }, [template]);

  const renderSection = (sectionId: string): React.ReactNode => {
    switch (sectionId) {
      case 'logo': return renderLogo();
      case 'company': return renderCompany();
      case 'client': return renderClient();
      case 'invoice': return renderInvoiceDetails();
      case 'project': return renderProject();
      case 'lineItems': return renderLineItems();
      case 'totals': return renderTotals();
      case 'footer': return renderFooter();
      default: return null;
    }
  };

  const renderLogo = (): React.ReactNode => {
    if (!state.showCompanyLogo) return null;
    const pos = positionClasses(state.logoPosition);
    return (
      <div className={`flex ${pos.flex}`}>
        <div className="h-20 w-auto max-w-[250px] rounded flex items-center justify-center">
          {state.companyLogo ? (
            <img src={state.companyLogo} alt="Company Logo" className="h-full w-auto max-w-[250px] object-contain" />
          ) : (
            <div className="h-20 w-48 bg-muted rounded flex items-center justify-center text-sm text-muted-foreground">
              Company Logo
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCompany = (): React.ReactNode => {
    if (!state.showCompanyName && !state.showCompanyAddress && !state.showCompanyPhone && !state.showCompanyVAT && !state.showCompanyRegistration) return null;
    const pos = positionClasses(state.companyPosition);
    return (
      <div className={`flex ${pos.flex}`}>
        <div className={`space-y-1 ${pos.text}`}>
          {state.showCompanyName && <div className="font-semibold">{previewData.company.name}</div>}
          {state.showCompanyAddress && <div className="text-sm text-muted-foreground whitespace-pre-line">{previewData.company.address}</div>}
          {state.showCompanyPhone && previewData.company.phone && (
            <div className="text-sm text-muted-foreground">
              {previewData.company.phone}{previewData.company.email ? ` | ${previewData.company.email}` : ''}
            </div>
          )}
          {state.showCompanyVAT && previewData.company.vatNumber && (
            <div className="text-sm text-muted-foreground">VAT Reg: {previewData.company.vatNumber}</div>
          )}
          {state.showCompanyRegistration && previewData.company.registrationNumber && (
            <div className="text-sm text-muted-foreground">Company Reg: {previewData.company.registrationNumber}</div>
          )}
        </div>
      </div>
    );
  };

  const renderClient = (): React.ReactNode => {
    if (!state.showClientName && !state.showClientAddress && !state.showClientContact) return null;
    const pos = positionClasses(state.clientPosition);
    return (
      <div className={`flex ${pos.flex}`}>
        <div className={`space-y-1 ${pos.text}`}>
          <div className="text-sm font-medium text-muted-foreground">Bill To:</div>
          {state.showClientName && <div className="font-medium">{previewData.client.name}</div>}
          {state.showClientAddress && <div className="text-sm text-muted-foreground whitespace-pre-line">{previewData.client.address}</div>}
          {state.showClientContact && previewData.client.contact && (
            <div className="text-sm text-muted-foreground">Contact: {previewData.client.contact}</div>
          )}
        </div>
      </div>
    );
  };

  const renderInvoiceDetails = (): React.ReactNode => {
    if (!state.showInvoiceNumber && !state.showInvoiceDate && !state.showDueDate) return null;
    const pos = positionClasses(state.invoiceDetailsPosition);
    return (
      <div className={`flex ${pos.flex}`}>
        <div className={`space-y-1 ${pos.text}`}>
          {state.showInvoiceNumber && <div className="text-2xl font-bold">{previewData.invoice.number}</div>}
          {state.showInvoiceDate && <div className="text-sm text-muted-foreground">Date: {previewData.invoice.date}</div>}
          {state.showDueDate && <div className="text-sm text-muted-foreground">Due: {previewData.invoice.dueDate}</div>}
        </div>
      </div>
    );
  };

  const renderProject = (): React.ReactNode => {
    if (!state.showProjectCode && !state.showProjectName && !state.showProjectDescription && !state.showProjectManager) return null;
    const pos = positionClasses(state.projectPosition);
    return (
      <div className={`flex ${pos.flex}`}>
        <div className={`space-y-1 ${pos.text}`}>
          {(state.showProjectCode || state.showProjectName) && (
            <div>
              <span className="text-sm text-muted-foreground">Project: </span>
              <span className="font-medium">{previewData.project.name}</span>
            </div>
          )}
          {state.showProjectDescription && previewData.project.description && (
            <div>
              <span className="text-sm text-muted-foreground">Description: </span>
              <span>{previewData.project.description}</span>
            </div>
          )}
          {state.showProjectManager && previewData.project.projectManager && (
            <div>
              <span className="text-sm text-muted-foreground">Project Manager: </span>
              <span>{previewData.project.projectManager}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderLineItems = (): React.ReactNode => {
    const pound = '\u00A3';
    const fmt = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Task 1.15 — single summary row per group
    const grouped = groupLineItems(previewData.lineItems, state.groupBy);

    // Task 2.8 — column definition map for ordered rendering
    type LineItem = typeof previewData.lineItems[number];
    type GroupItem = NonNullable<ReturnType<typeof groupLineItems>>[number];
    const colDefs: Record<string, {
      show: boolean; label: string; align: 'left' | 'right';
      cell: (item: LineItem) => React.ReactNode;
      summaryCell: (group: GroupItem) => React.ReactNode;
    }> = {
      date:           { show: state.showDate,           label: 'Date',            align: 'left',  cell: (i) => i.date,                               summaryCell: () => '' },
      description:    { show: state.showDescription,    label: 'Description',     align: 'left',  cell: (i) => i.description,                        summaryCell: (g) => {
        // Task 3.9b — pick template by group type, resolve tokens, strip HTML for plain-text render
        const tpl = getDescriptionTemplateForGroup(
          g.items,
          state.groupedRowDescription,
          state.groupedRowDescriptionExpense,
          state.groupedRowDescriptionPurchase,
        );
        const resolved = tpl
          ? resolveGroupLabel(tpl.replace(/<[^>]*>/g, ''), g.items, {
              totalQty: g.totalQty,
              subtotalAmount: g.subtotalAmount,
              itemCount: g.itemCount,
            })
          : g.groupLabel;
        return <span className="font-medium">{resolved || g.groupLabel}</span>;
      } },
      person:         { show: state.showPerson,         label: 'Person',          align: 'left',  cell: (i) => i.person || '-',                      summaryCell: () => '' },
      role:           { show: state.showRole,           label: 'Role',            align: 'left',  cell: (i) => i.role || '-',                        summaryCell: () => '' },
      qty:            { show: state.showQty,            label: 'Qty',             align: 'right', cell: (i) => i.qty.toFixed(1),                     summaryCell: () => '' },
      net:            { show: state.showRate,           label: 'Rate',            align: 'right', cell: (i) => `${pound}${fmt(i.net)}`,               summaryCell: () => '' },
      vat:            { show: state.showVAT,            label: 'VAT',             align: 'right', cell: (i) => `${pound}${fmt(i.vat)}`,               summaryCell: () => '' },
      vatCode:        { show: state.showVATCode,        label: 'VAT Code',        align: 'left',  cell: (i) => i.vatCode,                            summaryCell: () => '' },
      amount:         { show: state.showAmount,         label: 'Amount',          align: 'right', cell: (i) => `${pound}${fmt(i.amount)}`,            summaryCell: (g) => <span className="font-medium">{pound}{fmt(g.subtotalAmount)}</span> },
      costCentreCode: { show: state.showCostCentreCode, label: 'Cost Centre Code',align: 'left',  cell: (i) => i.costCentreCode,                     summaryCell: () => '' },
      costCentreName: { show: state.showCostCentreName, label: 'Cost Centre Name',align: 'left',  cell: (i) => i.costCentreName,                     summaryCell: () => '' },
    };

    // When groupBy is active, only Description and Amount are meaningful on
    // grouped summary rows — all other columns are suppressed (mirrors the
    // builder's isGroupedOut rule in InvoiceTemplateBuilder.tsx line ~1126).
    const isGroupedOut = (f: string) =>
      state.groupBy !== 'none' && f !== 'description' && f !== 'amount';

    const visibleCols = columnOrder.filter(f => colDefs[f]?.show && !isGroupedOut(f));

    return (
      <div className="border rounded-lg overflow-hidden">
        <table className={`w-full text-sm table-fixed ${state.showBorders ? 'border-collapse' : ''}`}>
          <thead style={{ backgroundColor: accentBg, color: accentText }}>
            <tr>
              {visibleCols.map(field => {
                const col = colDefs[field];
                return (
                  <th key={field} className={`p-2 whitespace-normal break-words ${col.align === 'right' ? 'text-right' : 'text-left'} ${state.showBorders ? 'border border-border' : ''}`}>
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {grouped === null
              ? previewData.lineItems.map((item, idx) => (
                  <tr key={item.id} className={state.showBorders ? '' : 'border-t'} style={{ backgroundColor: idx % 2 !== 0 ? accentRowBg : undefined }}>
                    {visibleCols.map(field => {
                      const col = colDefs[field];
                      return (
                        <td key={field} className={`p-2 ${field === 'date' ? 'whitespace-nowrap' : ''} ${col.align === 'right' ? 'text-right' : ''} ${state.showBorders ? 'border border-border' : ''}`}>
                          {col.cell(item)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              : grouped.map((group, idx) => (
                  <tr key={`group-summary-${group.groupKey}`} className={state.showBorders ? '' : 'border-t'} style={{ backgroundColor: idx % 2 !== 0 ? accentRowBg : undefined }}>
                    {visibleCols.map(field => {
                      const col = colDefs[field];
                      return (
                        <td key={field} className={`p-2 ${col.align === 'right' ? 'text-right' : ''} ${state.showBorders ? 'border border-border' : ''}`}>
                          {col.summaryCell(group)}
                        </td>
                      );
                    })}
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>
    );
  };

  const renderTotals = (): React.ReactNode => {
    const pound = '\u00A3';
    const fmt = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (
      <div className="flex justify-end">
        <div className="w-64 space-y-2">
          {state.showSubtotal && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal:</span>
              <span>{pound}{fmt(previewData.totals.subtotal)}</span>
            </div>
          )}
          {state.showTax && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">VAT (20%):</span>
              <span>{pound}{fmt(previewData.totals.tax)}</span>
            </div>
          )}
          {state.showDiscount && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Discount:</span>
              <span>-{pound}{fmt(previewData.totals.discount)}</span>
            </div>
          )}
          {state.showTotal && (
            <div className="flex justify-between font-bold text-lg border-t pt-2" style={{ borderColor: accentBg }}>
              <span style={{ color: accentBg }}>Total:</span>
              <span>{pound}{fmt(previewData.totals.total)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFooter = (): React.ReactNode => {
    if (!state.showBankDetails && !state.showNotes && !state.showTermsConditions) return null;
    const pos = positionClasses(state.footerPosition);
    return (
      <div className={`${state.showNotes ? '' : 'flex'} ${!state.showNotes ? pos.flex : ''} border-t pt-4`}>
        <div className={`space-y-3 text-sm text-muted-foreground ${!state.showNotes ? pos.text : ''} ${state.showNotes ? 'w-full' : ''}`}>
          {state.showBankDetails && (
            <div>
              <div className="font-medium text-foreground">Bank Details:</div>
              {previewData.company.bankDetails ? (
                <div className="space-y-0.5">
                  {previewData.company.bankDetails.bankName && <div>{previewData.company.bankDetails.bankName}</div>}
                  {previewData.company.bankDetails.accountName && <div>Account Name: {previewData.company.bankDetails.accountName}</div>}
                  {previewData.company.bankDetails.sortCode && <div>Sort Code: {previewData.company.bankDetails.sortCode}</div>}
                  {previewData.company.bankDetails.accountNumber && <div>Account No: {previewData.company.bankDetails.accountNumber}</div>}
                  {previewData.company.bankDetails.iban && <div>IBAN: {previewData.company.bankDetails.iban}</div>}
                  {previewData.company.bankDetails.swiftBic && <div>SWIFT/BIC: {previewData.company.bankDetails.swiftBic}</div>}
                </div>
              ) : (
                <div className="text-muted-foreground/60 italic">No bank details available</div>
              )}
            </div>
          )}
          {state.showNotes && (
            <div className="w-full">
              <div className="font-medium text-foreground mb-1">Notes:</div>
              <div
                className="border-2 border-dashed border-muted-foreground/40 rounded-md p-3 text-sm text-foreground min-h-[3em]"
                style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
              >
                {migrateNotes(state.notesContent)
                  ? <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: migrateNotes(state.notesContent) }} />
                  : <span className="text-muted-foreground/50">No notes</span>
                }
              </div>
            </div>
          )}
          {state.showTermsConditions && (
            <div>
              <div className="font-medium text-foreground">Terms &amp; Conditions:</div>
              {/* Task 4.3c/4.3d — render T&C HTML from template config.
                  In builder preview, merge chips stay as friendly placeholder labels
                  (no token resolution). Real values are resolved in Wizard Step 2. */}
              {state.termsContent ? (
                <div
                  className="prose prose-sm max-w-none [&_.merge-chip]:inline-block [&_.merge-chip]:bg-primary/10 [&_.merge-chip]:text-primary [&_.merge-chip]:border [&_.merge-chip]:border-primary/30 [&_.merge-chip]:rounded [&_.merge-chip]:px-1 [&_.merge-chip]:text-xs [&_.merge-chip]:font-medium"
                  dangerouslySetInnerHTML={{ __html: state.termsContent }}
                />
              ) : (
                <div className="italic text-muted-foreground/60">No terms and conditions entered.</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Task 5.3 / 5.8 — Backing Sheet: flat detail table using independent column config
  const renderBackingSheetLineItems = (): React.ReactNode => {
    const pound = '\u00A3';
    const fmt = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    type LineItem = typeof previewData.lineItems[number];
    // Task 5.8 — use backing sheet column visibility; fall back to all-visible when not configured
    const bsColVisible = (field: string): boolean => {
      if (Object.keys(state.backingSheetColumns).length === 0) return true; // default: all visible
      return state.backingSheetColumns[field] !== false;
    };
    const flatColDefs: Record<string, { label: string; align: 'left' | 'right'; cell: (item: LineItem) => React.ReactNode }> = {
      date:           { label: 'Date',             align: 'left',  cell: (i) => i.date },
      description:    { label: 'Description',      align: 'left',  cell: (i) => i.description },
      person:         { label: 'Person',           align: 'left',  cell: (i) => i.person || '-' },
      role:           { label: 'Role',             align: 'left',  cell: (i) => i.role || '-' },
      qty:            { label: 'Qty',              align: 'right', cell: (i) => i.qty.toFixed(1) },
      net:            { label: 'Rate',             align: 'right', cell: (i) => `${pound}${fmt(i.net)}` },
      vat:            { label: 'VAT',              align: 'right', cell: (i) => `${pound}${fmt(i.vat)}` },
      vatCode:        { label: 'VAT Code',         align: 'left',  cell: (i) => i.vatCode },
      amount:         { label: 'Amount',           align: 'right', cell: (i) => `${pound}${fmt(i.amount)}` },
      costCentreCode: { label: 'Cost Centre Code', align: 'left',  cell: (i) => i.costCentreCode },
      costCentreName: { label: 'Cost Centre Name', align: 'left',  cell: (i) => i.costCentreName },
    };
    // Task 5.8 — use independent backing sheet column order + visibility
    const bsCols = state.backingSheetColumnOrder.filter(f => flatColDefs[f] && bsColVisible(f));
    return (
      <div className="border rounded-lg overflow-hidden">
        <table className={`w-full text-sm table-fixed ${state.showBorders ? 'border-collapse' : ''}`}>
          <thead style={{ backgroundColor: accentBg, color: accentText }}>
            <tr>
              {bsCols.map(field => {
                const col = flatColDefs[field];
                return (
                  <th key={field} className={`p-2 whitespace-normal break-words ${col.align === 'right' ? 'text-right' : 'text-left'} ${state.showBorders ? 'border border-border' : ''}`}>
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {previewData.lineItems.map((item, idx) => (
              <tr key={item.id} className={state.showBorders ? '' : 'border-t'} style={{ backgroundColor: idx % 2 !== 0 ? accentRowBg : undefined }}>
                {bsCols.map(field => {
                  const col = flatColDefs[field];
                  return (
                    <td key={field} className={`p-2 ${col.align === 'right' ? 'text-right' : ''} ${state.showBorders ? 'border border-border' : ''}`}>
                      {col.cell(item)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const showBackingSheet = state.includeBackingSheet;

  return (
    <div className="space-y-6">
      {/* Main invoice page */}
      <div
        className="bg-white p-8 rounded-lg shadow-sm border mx-auto"
        style={{ width: paperWidth, minHeight: paperMinHeight, fontFamily: fontStack }} // Task 6.6c
      >
        <div className="space-y-6">
          {state.sectionOrder.map((sectionId, idx) => {
            const content = renderSection(sectionId);
            if (!content) return null;
            // Task 6.5g — accent-coloured divider between header sections and body
            const nextId = state.sectionOrder[idx + 1];
            const isLastHeaderSection =
              (sectionId === 'project' || sectionId === 'invoice' || sectionId === 'client') &&
              nextId === 'lineItems';
            return (
              <React.Fragment key={sectionId}>
                {content}
                {isLastHeaderSection && (
                  <hr style={{ borderColor: accentBg, borderTopWidth: 2, margin: 0 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Task 5.3 / 5.8 — Backing Sheet page (independent orientation) */}
      {showBackingSheet && (
        <>
          {/* Page break divider */}
          <div className="flex items-center gap-3 px-2">
            <div className="flex-1 border-t border-dashed border-muted-foreground/40" />
            <span className="text-xs text-muted-foreground/60 font-medium tracking-wide uppercase whitespace-nowrap">
              — Backing Sheet —
            </span>
            <div className="flex-1 border-t border-dashed border-muted-foreground/40" />
          </div>

          {/* Backing Sheet A4 page — uses backingSheetOrientation, independent of main invoice */}
          <div
            className="bg-white p-8 rounded-lg shadow-sm border mx-auto"
            style={{
              width: state.backingSheetOrientation === 'landscape' ? '297mm' : '210mm',
              minHeight: state.backingSheetOrientation === 'landscape' ? '210mm' : '297mm',
              fontFamily: fontStack, // Task 6.6c
            }}
          >
            <div className="space-y-4">
              {/* Independent 3-column header grid */}
              <div className="grid grid-cols-3 gap-4 items-start">
                {/* Left: Logo + Company */}
                <div className="space-y-1 min-w-0">
                  {state.bsShowLogo && previewData.company.logo && (
                    <img src={previewData.company.logo} alt="Logo" className="h-12 w-auto max-w-[120px] object-contain mb-1" />
                  )}
                  {state.bsShowCompanyName && (
                    <div className="font-semibold text-sm">{previewData.company.name}</div>
                  )}
                  {state.bsShowCompanyAddress && (
                    <div className="text-xs text-muted-foreground whitespace-pre-line">{previewData.company.address}</div>
                  )}
                </div>
                {/* Centre: Backing Sheet label + Invoice ref */}
                <div className="text-center space-y-1 min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Backing Sheet</div>
                  {state.bsShowInvoiceNumber && <div className="font-bold text-sm">{previewData.invoice.number}</div>}
                  {state.bsShowInvoiceDate && <div className="text-xs text-muted-foreground">Date: {previewData.invoice.date}</div>}
                </div>
                {/* Right: Client */}
                <div className="text-right space-y-1 min-w-0">
                  {(state.bsShowClientName || state.bsShowClientAddress) && (
                    <div className="text-xs text-muted-foreground font-medium">Bill To:</div>
                  )}
                  {state.bsShowClientName && <div className="font-medium text-sm">{previewData.client.name}</div>}
                  {state.bsShowClientAddress && (
                    <div className="text-xs text-muted-foreground whitespace-pre-line">{previewData.client.address}</div>
                  )}
                </div>
              </div>

              {/* Full flat detail table */}
              {renderBackingSheetLineItems()}

              {/* Independent BS footer */}
              {(state.bsShowBankDetails || state.bsShowNotes || state.bsShowTermsConditions) && (
                <div className="border-t pt-4 space-y-3 text-sm text-muted-foreground">
                  {state.bsShowBankDetails && previewData.company.bankDetails && (
                    <div>
                      <div className="font-medium text-foreground text-xs">Bank Details:</div>
                      <div className="text-xs space-y-0.5">
                        {previewData.company.bankDetails.bankName && <div>{previewData.company.bankDetails.bankName}</div>}
                        {previewData.company.bankDetails.sortCode && <div>Sort Code: {previewData.company.bankDetails.sortCode}</div>}
                        {previewData.company.bankDetails.accountNumber && <div>Account No: {previewData.company.bankDetails.accountNumber}</div>}
                      </div>
                    </div>
                  )}
                  {state.bsShowNotes && state.notesContent && (
                    <div>
                      <div className="font-medium text-foreground text-xs">Notes:</div>
                      <div className="prose prose-xs max-w-none text-xs" dangerouslySetInnerHTML={{ __html: migrateNotes(state.notesContent) }} />
                    </div>
                  )}
                  {state.bsShowTermsConditions && state.termsContent && (
                    <div>
                      <div className="font-medium text-foreground text-xs">Terms &amp; Conditions:</div>
                      <div className="prose prose-xs max-w-none text-xs" dangerouslySetInnerHTML={{ __html: state.termsContent }} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Standard Format Preview
// Used for mock templates with section types: header, summary, line-items, etc.
// ============================================================================

function StandardFormatPreview({ template, previewData }: InvoicePreviewProps) {
  // Use the same extractBuilderState logic so column visibility is resolved
  // from the template's saved state (whether array or object format), not the
  // raw section config which may have all columns visible:true in mock data.
  const state = useMemo(() => extractBuilderState(template), [template]);

  // Task 2.3 — orientation from template settings
  const orientation = template.settings?.orientation || 'portrait';
  const paperWidth = orientation === 'landscape' ? '297mm' : '210mm';
  const paperMinHeight = orientation === 'landscape' ? '210mm' : '297mm';

  // Task 6.5 — accent colour (StandardFormatPreview)
  const sfpAccentBg = template.branding?.primaryColor || '#2563eb';
  const sfpAccentText = getContrastTextColor(sfpAccentBg);
  // Task 6.7c — 5% accent tint for alternate rows (StandardFormatPreview)
  const sfpAccentRowBg = state.alternateRowColors ? hexToRgba(sfpAccentBg, 0.05) : 'transparent';

  // Task 6.6c — font family
  const sfpFontKey = template.branding?.fontFamily || 'inter';
  const sfpFontStack = FONT_STACKS[sfpFontKey] ?? sfpFontKey;

  const visibleSections = template.sections
    .filter((s) => s.visible)
    .sort((a, b) => a.order - b.order);

  const renderSection = (section: TemplateSection) => {
    switch (section.type) {
      case "header":
        return renderHeaderSection(section.config as HeaderSectionConfig);
      case "summary":
        return renderSummarySection(section.config as SummarySectionConfig);
      case "line-items":
        return renderLineItemsSection(section.config as LineItemsSectionConfig);
      case "totals":
        return renderTotalsSection(section.config as TotalsSectionConfig);
      case "footer":
        return renderFooterSection(section.config as FooterSectionConfig);
      default:
        return null;
    }
  };

  const renderHeaderSection = (config: HeaderSectionConfig) => {
    return (
      <div key="header" className="space-y-6">
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-2">
            {previewData.company.logo && (
              <img src={previewData.company.logo} alt="Company Logo" className="h-16 mb-4" />
            )}
            {config.showCompanyName && (
              <h3 className="font-semibold text-lg">{previewData.company.name}</h3>
            )}
            {config.showCompanyAddress && (
              <div className="text-sm text-muted-foreground whitespace-pre-line">{previewData.company.address}</div>
            )}
            {config.showCompanyPhone && previewData.company.phone && (
              <div className="text-sm text-muted-foreground">Tel: {previewData.company.phone}</div>
            )}
            {config.showCompanyEmail && previewData.company.email && (
              <div className="text-sm text-muted-foreground">{previewData.company.email}</div>
            )}
            {config.showVAT && previewData.company.vatNumber && (
              <div className="text-sm text-muted-foreground">VAT: {previewData.company.vatNumber}</div>
            )}
            {config.showRegistration && previewData.company.registrationNumber && (
              <div className="text-sm text-muted-foreground">Reg No: {previewData.company.registrationNumber}</div>
            )}
          </div>
          <div className="space-y-2 text-right">
            <h4 className="font-semibold text-sm text-muted-foreground">INVOICE TO</h4>
            {config.showClientName && (
              <div className="font-semibold">{previewData.client.name}</div>
            )}
            {config.showClientContact && previewData.client.contact && (
              <div className="text-sm text-muted-foreground">Attn: {previewData.client.contact}</div>
            )}
            {config.showClientAddress && (
              <div className="text-sm text-muted-foreground whitespace-pre-line">{previewData.client.address}</div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          {config.showInvoiceNumber && (
            <div>
              <span className="font-semibold">Invoice Number:</span>{" "}
              {previewData.invoice.number}
            </div>
          )}
          {config.showInvoiceDate && (
            <div>
              <span className="font-semibold">Invoice Date:</span>{" "}
              {previewData.invoice.date}
            </div>
          )}
          {config.showDueDate && (
            <div>
              <span className="font-semibold">Due Date:</span>{" "}
              {previewData.invoice.dueDate}
            </div>
          )}
          {config.showPONumber && previewData.invoice.poNumber && (
            <div>
              <span className="font-semibold">PO Number:</span>{" "}
              {previewData.invoice.poNumber}
            </div>
          )}
        </div>
        <Separator />
      </div>
    );
  };

  const renderSummarySection = (config: SummarySectionConfig) => {
    return (
      <div key="summary" className="space-y-4">
        {config.showProjectName && (
          <div>
            <h4 className="font-semibold text-lg">{previewData.project.name}</h4>
          </div>
        )}
        {config.showProjectDescription && previewData.project.description && (
          <div className="text-sm">{previewData.project.description}</div>
        )}
        {config.showBillingPeriod && previewData.billingPeriod && (
          <div className="text-sm text-muted-foreground">
            Period: {previewData.billingPeriod.start} - {previewData.billingPeriod.end}
          </div>
        )}
        <Separator />
      </div>
    );
  };

  const renderLineItemsSection = (_config: LineItemsSectionConfig) => {
    // Derive visible columns from the resolved `state` (extractBuilderState)
    // rather than raw config.columns — this ensures toggles made in the builder
    // are reflected immediately, and mock templates don't show all columns by default.
    const FIELD_LABELS: Record<string, string> = {
      date: 'Date', description: 'Description', person: 'Person',
      role: 'Role', qty: 'Qty', net: 'Rate', vat: 'VAT',
      vatCode: 'VAT Code', amount: 'Amount',
      costCentreCode: 'Cost Centre Code', costCentreName: 'Cost Centre Name',
    };
    const FIELD_SHOW: Array<[string, keyof BuilderState]> = [
      ['date', 'showDate'], ['description', 'showDescription'], ['person', 'showPerson'],
      ['role', 'showRole'], ['qty', 'showQty'], ['net', 'showRate'],
      ['vat', 'showVAT'], ['vatCode', 'showVATCode'], ['amount', 'showAmount'],
      ['costCentreCode', 'showCostCentreCode'], ['costCentreName', 'showCostCentreName'],
    ];
    const visibleColumns = FIELD_SHOW
      .filter(([, key]) => state[key])
      .map(([field]) => ({ field, label: FIELD_LABELS[field] || field }));

    return (
      <div key="line-items" className="space-y-4">
        <h4 className="font-semibold">Line Items</h4>
        <Table>
          <TableHeader>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead key={col.field} className={col.field === 'description' ? 'w-[40%]' : ''}>
                  {col.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {previewData.lineItems.map((item, idx) => (
              <TableRow key={item.id} style={{ backgroundColor: idx % 2 !== 0 ? sfpAccentRowBg : undefined }}>
                {visibleColumns.map((col) => (
                  <TableCell key={col.field}>
                    {renderLineItemCell(item, col.field)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Separator />
      </div>
    );
  };

  const renderLineItemCell = (
    item: (typeof previewData.lineItems)[0],
    field: string
  ) => {
    switch (field) {
      case "date":
        return item.date;
      case "description":
        return item.description;
      case "person":
        return item.person || "-";
      case "role":
        return item.role || "-";
      case "phase":
        return item.phase || "-";
      case "task":
        return item.task || "-";
      case "cost-center":
        return item.costCentreCode || item.costCentreName || "-";
      case "qty":
        return item.qty.toFixed(2);
      case "rate":
        return item.rate != null ? `\u00A3${item.rate.toFixed(2)}` : "-";
      case "net":
        return `\u00A3${item.net.toFixed(2)}`;
      case "vat-rate":
        return item.vatCode || "-";
      case "vat":
        return `\u00A3${item.vat.toFixed(2)}`;
      case "amount":
        return `\u00A3${item.amount.toFixed(2)}`;
      default:
        return "-";
    }
  };

  const renderTotalsSection = (config: TotalsSectionConfig) => {
    return (
      <div key="totals" className="space-y-2">
        <div className="flex justify-end">
          <div className="w-64 space-y-2">
            {config.showSubtotal && (
              <div className="flex justify-between text-sm">
                <span className="font-semibold">Subtotal:</span>
                <span>{'\u00A3'}{previewData.totals.subtotal.toFixed(2)}</span>
              </div>
            )}
            {config.showTax && (
              <div className="flex justify-between text-sm">
                <span className="font-semibold">VAT (20%):</span>
                <span>{'\u00A3'}{previewData.totals.tax.toFixed(2)}</span>
              </div>
            )}
            <Separator />
            {config.showTotal && (
              <div className="flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span>{'\u00A3'}{previewData.totals.total.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFooterSection = (config: FooterSectionConfig) => {
    const hasContent = config.showNotes || config.showTermsAndConditions || config.showBankDetails;
    if (!hasContent) return null;

    return (
      <div key="footer" className="mt-8">
        <Separator className="mb-4" />
        <div className="space-y-3 text-sm text-muted-foreground">
          {config.showBankDetails && (
            <div>
              <div className="font-medium text-foreground">Bank Details:</div>
              {previewData.company.bankDetails ? (
                <div className="space-y-0.5">
                  {previewData.company.bankDetails.bankName && <div>{previewData.company.bankDetails.bankName}</div>}
                  {previewData.company.bankDetails.accountName && <div>Account Name: {previewData.company.bankDetails.accountName}</div>}
                  {previewData.company.bankDetails.sortCode && <div>Sort Code: {previewData.company.bankDetails.sortCode}</div>}
                  {previewData.company.bankDetails.accountNumber && <div>Account No: {previewData.company.bankDetails.accountNumber}</div>}
                  {previewData.company.bankDetails.iban && <div>IBAN: {previewData.company.bankDetails.iban}</div>}
                  {previewData.company.bankDetails.swiftBic && <div>SWIFT/BIC: {previewData.company.bankDetails.swiftBic}</div>}
                </div>
              ) : (
                <div className="text-muted-foreground/60 italic">No bank details available</div>
              )}
            </div>
          )}
          {config.showNotes && config.notes && (
            <div>
              <div className="font-medium text-foreground">Notes:</div>
              <div>{config.notes}</div>
            </div>
          )}
          {config.showTermsAndConditions && config.termsAndConditions && (
            <div>
              <div className="font-medium text-foreground">Terms &amp; Conditions:</div>
              <div
                className="prose prose-sm max-w-none [&_.merge-chip]:inline-block [&_.merge-chip]:bg-primary/10 [&_.merge-chip]:text-primary [&_.merge-chip]:border [&_.merge-chip]:border-primary/30 [&_.merge-chip]:rounded [&_.merge-chip]:px-1 [&_.merge-chip]:text-xs [&_.merge-chip]:font-medium"
                dangerouslySetInnerHTML={{ __html: config.termsAndConditions }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Task 5.3 / 5.8 — Backing Sheet flat detail table for StandardFormatPreview (independent column config)
  const renderStandardBackingSheetLineItems = (): React.ReactNode => {
    const pound = '\u00A3';
    const fmt = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    type LineItem = typeof previewData.lineItems[number];

    // Task 5.8 — use backingSheetColumns visibility; fall back to all-visible when not configured
    const bsColVisible = (field: string): boolean => {
      if (Object.keys(state.backingSheetColumns).length === 0) return true;
      return state.backingSheetColumns[field] !== false;
    };

    const flatColDefs: Record<string, { label: string; align: 'left' | 'right'; cell: (item: LineItem) => React.ReactNode }> = {
      date:           { label: 'Date',             align: 'left',  cell: (i) => i.date },
      description:    { label: 'Description',      align: 'left',  cell: (i) => i.description },
      person:         { label: 'Person',           align: 'left',  cell: (i) => i.person || '-' },
      role:           { label: 'Role',             align: 'left',  cell: (i) => i.role || '-' },
      qty:            { label: 'Qty',              align: 'right', cell: (i) => i.qty.toFixed(1) },
      net:            { label: 'Rate',             align: 'right', cell: (i) => `${pound}${fmt(i.net)}` },
      vat:            { label: 'VAT',              align: 'right', cell: (i) => `${pound}${fmt(i.vat)}` },
      vatCode:        { label: 'VAT Code',         align: 'left',  cell: (i) => i.vatCode },
      amount:         { label: 'Amount',           align: 'right', cell: (i) => `${pound}${fmt(i.amount)}` },
      costCentreCode: { label: 'Cost Centre Code', align: 'left',  cell: (i) => i.costCentreCode },
      costCentreName: { label: 'Cost Centre Name', align: 'left',  cell: (i) => i.costCentreName },
    };
    // Task 5.8 — use independent backing sheet column order + visibility
    const bsCols = state.backingSheetColumnOrder.filter(f => flatColDefs[f] && bsColVisible(f));
    return (
      <div className="border rounded-lg overflow-hidden overflow-x-auto">
        <table className={`w-full text-sm ${state.showBorders ? 'border-collapse' : ''}`}>
          <thead style={{ backgroundColor: sfpAccentBg, color: sfpAccentText }}> {/* Task 6.5/6.6 */}
            <tr>
              {bsCols.map(field => {
                const col = flatColDefs[field];
                return (
                  <th key={field} className={`p-2 whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'} ${state.showBorders ? 'border border-border' : ''}`}>
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {previewData.lineItems.map((item, idx) => (
              <tr key={item.id} className={state.showBorders ? '' : 'border-t'} style={{ backgroundColor: idx % 2 !== 0 ? sfpAccentRowBg : undefined }}>
                {bsCols.map(field => {
                  const col = flatColDefs[field];
                  return (
                    <td key={field} className={`p-2 ${field === 'date' ? 'whitespace-nowrap' : ''} ${col.align === 'right' ? 'text-right' : ''} ${state.showBorders ? 'border border-border' : ''}`}>
                      {col.cell(item)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const stdShowBackingSheet = state.includeBackingSheet;

  // Header sections for the Backing Sheet repeated header
  const headerSection = visibleSections.find(s => s.type === 'header');

  return (
    <div className="space-y-6">
      {/* Main invoice page */}
      <div
        className="bg-white p-8 rounded-lg shadow-sm border mx-auto"
        style={{ width: paperWidth, minHeight: paperMinHeight, fontFamily: sfpFontStack }} // Task 6.6c
      >
        <div className="space-y-6">
          {visibleSections.map((section) => (
            <React.Fragment key={section.id}>
              {renderSection(section)}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Task 5.3 — Backing Sheet page */}
      {stdShowBackingSheet && (
        <>
          {/* Page break divider */}
          <div className="flex items-center gap-3 px-2">
            <div className="flex-1 border-t border-dashed border-muted-foreground/40" />
            <span className="text-xs text-muted-foreground/60 font-medium tracking-wide uppercase whitespace-nowrap">
              — Backing Sheet —
            </span>
            <div className="flex-1 border-t border-dashed border-muted-foreground/40" />
          </div>

          {/* Backing Sheet A4 page — uses backingSheetOrientation, independent of main invoice */}
          <div
            className="bg-white p-8 rounded-lg shadow-sm border mx-auto"
            style={{
              width: state.backingSheetOrientation === 'landscape' ? '297mm' : '210mm',
              minHeight: state.backingSheetOrientation === 'landscape' ? '210mm' : '297mm',
              fontFamily: sfpFontStack, // Task 6.6c
            }}
          >
            <div className="space-y-6">
              {/* Repeated header */}
              {headerSection && renderHeaderSection(headerSection.config as HeaderSectionConfig)}
              <div className="text-sm font-medium text-muted-foreground">— Backing Sheet</div>

              {/* Full flat detail table */}
              {renderStandardBackingSheetLineItems()}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
